
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * 生成带用户 ID 的 ProTable 列状态持久化 key，
 * 确保不同用户的列配置互相隔离。
 * 用户 ID 自动从 localStorage 读取，无需在组件内额外传参。
 *
 * @param key  页面唯一标识，如 'org_user_columns'
 */
export const getColumnsStateKey = (key: string) => {
  const userId = localStorage.getItem('userId') ?? 'default';
  return `${key}_${userId}`;
};

const getColumnsStateStoreKey = () => {
  const userId = localStorage.getItem('userId') ?? 'default';
  return `protable_columns_state_${userId}`;
};

type ColumnStateItem = {
  show?: boolean;
  fixed?: 'left' | 'right';
  order?: number;
};

type ColumnsStateMap = Record<string, ColumnStateItem>;
type ColumnsStateStore = Record<string, ColumnsStateMap>;

const normalizeColumnsStateMap = (
  input?: Record<string, any>,
): ColumnsStateMap => {
  if (!input || typeof input !== 'object') return {};
  const normalized: ColumnsStateMap = {};

  Object.entries(input).forEach(([k, v]) => {
    if (!v || typeof v !== 'object') return;
    const item: ColumnStateItem = {};
    if (v.show === false) item.show = false;
    if (v.fixed === 'left' || v.fixed === 'right') item.fixed = v.fixed;
    if (typeof v.order === 'number') item.order = v.order;
    if (
      item.show === false ||
      item.fixed !== undefined ||
      typeof item.order === 'number'
    ) {
      normalized[k] = item;
    }
  });

  return normalized;
};

const readColumnsStateStore = (): ColumnsStateStore => {
  try {
    const raw = localStorage.getItem(getColumnsStateStoreKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ColumnsStateStore;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeColumnsStateStore = (store: ColumnsStateStore) => {
  try {
    const storeKey = getColumnsStateStoreKey();
    if (!store || Object.keys(store).length === 0) {
      localStorage.removeItem(storeKey);
      return;
    }
    localStorage.setItem(storeKey, JSON.stringify(store));
  } catch {
    // ignore localStorage write errors
  }
};

const hasCustomizedColumnsState = (state?: ColumnsStateMap): boolean => {
  if (!state || typeof state !== 'object') return false;
  return Object.values(state).some((item) => {
    if (!item) return false;
    return (
      item.show === false ||
      item.fixed !== undefined ||
      typeof item.order === 'number'
    );
  });
};

/**
 * ProTable 列状态持久化配置（懒写入）：
 * - 初次进入页面不会自动创建 localStorage key
 * - 仅当用户实际调整列显示/固定/顺序后才写入
 * - 配置恢复为默认时自动删除对应 key
 */
export const useColumnsStatePersistence = (key: string) => {
  const stateKey = key;
  const read = (): ColumnsStateMap => {
    const store = readColumnsStateStore();
    return normalizeColumnsStateMap(store[stateKey] as Record<string, any>);
  };
  const [value, setValue] = useState<ColumnsStateMap>(() => read());

  const onChange = useCallback((state?: ColumnsStateMap) => {
      const store = readColumnsStateStore();
      // 受控 value 保留 ProTable 回传的原始状态，避免重置/排序时 UI 不同步
      const nextValue = (state ?? {}) as ColumnsStateMap;
      setValue(nextValue);
      const normalized = normalizeColumnsStateMap(state as Record<string, any>);

      if (!hasCustomizedColumnsState(normalized)) {
        delete store[stateKey];
        writeColumnsStateStore(store);
        return;
      }
      store[stateKey] = normalized;
      writeColumnsStateStore(store);
  }, [stateKey]);

  return useMemo(() => ({
    value,
    onChange,
  }), [value, onChange]);
};

type ViewModePersistenceOptions<T extends string> = {
  key: string;
  defaultValue: T;
  persistedValue: T;
  withUserId?: boolean;
};

const getViewModeStoreKey = (key: string, withUserId?: boolean) => {
  if (!withUserId) return key;
  const userId = localStorage.getItem('userId') ?? 'default';
  return `${key}_${userId}`;
};

/**
 * 视图模式持久化（懒写入）：
 * - 仅当模式等于 persistedValue 时写入 localStorage
 * - 切回默认模式时删除 key
 * - 支持按用户隔离 key（withUserId）
 */
export const useViewModePersistence = <T extends string>({
  key,
  defaultValue,
  persistedValue,
  withUserId = false,
}: ViewModePersistenceOptions<T>) => {
  const [value, setValue] = useState<T>(() => {
    try {
      const storeKey = getViewModeStoreKey(key, withUserId);
      const cached = localStorage.getItem(storeKey);
      return cached === persistedValue ? persistedValue : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      const storeKey = getViewModeStoreKey(key, withUserId);
      if (value === persistedValue) {
        localStorage.setItem(storeKey, persistedValue);
      } else {
        localStorage.removeItem(storeKey);
      }
    } catch {
      // ignore localStorage write errors
    }
  }, [key, persistedValue, value, withUserId]);

  return [value, setValue] as const;
};

/**
 * 清理历史遗留的空列配置缓存（一次性/可重复执行）：
 * - 仅删除从未做过列自定义的 key
 * - 保留用户真实配置过的列状态
 */
export const cleanupRedundantColumnsStateKeys = () => {
  if (typeof window === 'undefined') return;
  const store = readColumnsStateStore();
  let changed = false;

  // 清理总 key 内部的冗余项（未自定义）
  Object.keys(store).forEach((stateKey) => {
    const current = normalizeColumnsStateMap(store[stateKey] as Record<string, any>);
    if (!hasCustomizedColumnsState(current)) {
      delete store[stateKey];
      changed = true;
      return;
    }
    store[stateKey] = current;
  });

  if (changed) {
    writeColumnsStateStore(store);
  }
};

/**
 * 从 localStorage 读取 ProTable 列状态，返回当前被隐藏（show: false）的列 dataIndex 集合。
 * 用于导出时过滤用户自定义隐藏的列。
 *
 * @param key  页面唯一标识，与 getColumnsStateKey 保持一致
 */
export const getHiddenColumnsSet = (key: string): Set<string> => {
  try {
    const store = readColumnsStateStore();
    const state = normalizeColumnsStateMap(store[key] as Record<string, any>);
    if (!state || typeof state !== 'object') return new Set();
    return new Set(
      Object.entries(state)
        .filter(([, v]) => v.show === false)
        .map(([k]) => k),
    );
  } catch {
    return new Set();
  }
};

/** 应用类型 */
export const appTypeMap = (t?: (key: string, defaultValue?: string) => string) => ({
  "next-chat": t?.(`menu.app.next-chat`, "聊天"),
  "next-search": t?.(`menu.app.next-search`, "搜索"),
  "agent": t?.(`menu.app.agent`, "智能体"),
  "memory": t?.(`menu.app.memory`, "记忆体"),
})
